/** Hoje: dois mostradores (saldo de hoje e fim do mês), o aviso do menor saldo, a escala,
 * a fita de 30 dias, o diário e a lista do que há para pagar.
 *
 * Não existe "pode gastar até X": o painel diz onde aperta, não convida a gastar.
 */

import { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Scale } from "../components/Scale";
import { useToast } from "../components/Toast";
import { Tape } from "../components/Tape";
import {
  AccountTag,
  Empty,
  ErrorBanner,
  Face,
  Key,
  Field,
  Label,
  Seg,
  Skeleton,
  Stamp,
  Zone,
  zoneClass,
} from "../components/ui";
import { addDays, column, dayMonth, dayMonthYear, dayOnly, daysInMonth, hojeLocal, money, moneyAbs, monthRange, parseMoney, stampLabel, sumMoney, weekday } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { useSimulation } from "../lib/simulation";
import {
  useAccounts,
  useClearTransaction,
  useConfirmTransaction,
  useProjection,
  useSettingsQuery,
  useToday,
} from "../lib/queries";
import type { Day, DayItem, HealthLevel, ZoneColor } from "../lib/types";

type Janela = "hoje" | "semana" | "mes";

const JANELAS: { id: Janela; label: string }[] = [
  { id: "hoje", label: "Hoje" },
  { id: "semana", label: "Semana" },
  { id: "mes", label: "Mês" },
];

/** Altura mínima de alvo no dedo; a tecla do sistema para em 32px. */
const ALVO = { minHeight: 44 };

export function Hoje() {
  const { scope } = usePrefs();
  const sim = useSimulation();
  const nav = useNavigate();
  const [janela, setJanela] = useState<Janela>("semana");
  const [confirmando, setConfirmando] = useState<DayItem | null>(null);
  const aviso = useToast();

  const hojeISO = hojeLocal();
  const { last } = monthRange(hojeISO);
  // Vai até o fim do mês seguinte: o fim do mês corrente precisa dos dias entre hoje e lá,
  // e a fita mostra 30 dias que podem atravessar o mês.
  const { data, isPending, error, refetch } = useProjection({
    scope,
    frm: hojeISO,
    to: addDays(last, 31),
    scenario_id: sim.scenarioId,
  });

  const clear = useClearTransaction();
  const confirm = useConfirmTransaction();
  // O resumo do motor. Aqui ele entra pelo colchão e pelo horizonte — os dois números que
  // a janela curta da projeção acima não tem como dar.
  const resumo = useToday(scope);
  const { data: settings } = useSettingsQuery();
  const { data: accounts } = useAccounts();
  const accById = useMemo(
    () => new Map((accounts ?? []).map((a) => [a.id, a])),
    [accounts],
  );

  if (isPending) return <section className="view on"><Skeleton lines={6} /></section>;
  if (error)
    return (
      <section className="view on">
        <ErrorBanner message={(error as Error).message} onRetry={() => refetch()} />
      </section>
    );
  if (!data) return null;

  const today = data.today;
  const dias = data.days;
  const diaHoje = dias.find((d) => d.date === today) ?? dias[0];
  const fimDoMes = dias.find((d) => d.date === monthRange(today).last) ?? dias[dias.length - 1];

  // As duas leituras do painel. O motor já entregou os saldos; aqui é seleção, não cálculo.
  const saldoHoje = data.realized_balance;
  const zonaHoje = diaHoje?.color ?? "amber";

  // Onde o aviso do menor saldo fecha a janela. Escolher o DIA é do cliente; o saldo desse
  // trecho continua sendo conta do motor (ver o comentário de <MenorSaldo>).
  const entrada = primeiraEntrada(dias, today);

  const aPagar = itensParaPagar(dias, today, janela);
  const totalAPagar = sumMoney(aPagar.map((i) => (i.kind === "income" ? -i.amount : i.amount)));
  const limite = janela === "hoje" ? today : janela === "semana" ? addDays(today, 7) : monthRange(today).last;

  const aReceber = sumMoney(
    dias
      .filter((d) => d.date >= today && d.date <= monthRange(today).last)
      .flatMap((d) => d.items)
      .filter((i) => i.kind === "income" && i.status !== "cleared")
      .map((i) => i.amount),
  );
  const aPagarMes = sumMoney(
    dias
      .filter((d) => d.date >= today && d.date <= monthRange(today).last)
      .flatMap((d) => d.items)
      .filter((i) => i.kind !== "income" && i.status !== "cleared")
      .map((i) => i.amount),
  );

  const diarioDoDia = diaHoje ? diaHoje.daily_budget : 0;
  const diarioMensal = settings?.settings.daily_budget ?? 0;
  const porDia = diarioMensal > 0 ? diarioMensal / daysInMonth(today) : null;
  // O gasto do dia vem dos lançamentos, não do que sobrou do orçamento. O motor trava o
  // que resta em zero (nunca devolve negativo), então `porDia − resta` parava no teto:
  // com R$ 96,77 de orçamento e R$ 300 gastos, a tela dizia "gasto hoje R$ 96,77".
  const gastoHoje = sumMoney(
    (diaHoje?.items ?? [])
      .filter((i) => !i.synthetic && i.bucket === "daily" && i.kind === "expense")
      .map((i) => i.amount),
  );
  const estourou = porDia !== null && gastoHoje > porDia;

  return (
    <section className="view on" aria-label="Hoje">
      {data.warnings.map((w) => (
        <ErrorBanner key={w} message={w} />
      ))}

      <div className="hoje">
        <div className="meters">
          <Face className="meter" zone={zonaHoje as ZoneColor}>
            <div className="lblrow">
              <Label>Saldo hoje</Label>
              <Zone color={zonaHoje as ZoneColor} levels={settings?.settings.health_levels} value={saldoHoje} />
            </div>
            <div className={`reading t-${zoneClass(zonaHoje as ZoneColor)}`}>{money(saldoHoje)}</div>
            <button type="button" className="causelink" onClick={() => nav("/contas")}>
              Ver contas
            </button>
          </Face>

          <Face className="meter min" zone={fimDoMes.color}>
            <div className="lblrow">
              <Label>Fim do mês · {dayMonth(fimDoMes.date)}</Label>
              <Zone color={fimDoMes.color} levels={settings?.settings.health_levels} value={fimDoMes.end} />
            </div>
            <div className={`reading t-${zoneClass(fimDoMes.color)}`}>{money(fimDoMes.end)}</div>
            <div className="sub">
              {aPagarMes > 0 && (
                <span className="pair">
                  a pagar <span className="num">−{money(aPagarMes)}</span>
                </span>
              )}
              {aPagarMes > 0 && aReceber > 0 && " · "}
              {aReceber > 0 && (
                <span className="pair">
                  a receber <span className="num t-in">+{money(aReceber)}</span>
                </span>
              )}
              {aPagarMes === 0 && aReceber === 0 && "nada previsto até o fim do mês"}
            </div>
          </Face>

          {/* O menor saldo até o próximo dinheiro entrar — a promessa do PRODUCT.md.
              Ocupa a largura inteira do grupo: é um aviso, não um terceiro mostrador
              disputando a leitura de abertura. O `gridColumn` fica aqui porque é
              posicionamento de uma peça só, não vocabulário novo do design.css. */}
          <div style={{ gridColumn: "1 / -1" }}>
            {entrada ? (
              <MenorSaldo
                scope={scope}
                de={today}
                ate={entrada}
                scenarioId={sim.scenarioId}
                levels={settings?.settings.health_levels}
                colchao={resumo.data?.buffer}
              />
            ) : (
              <SemEntrada
                limite={dias[dias.length - 1]?.date ?? today}
                horizonte={resumo.data?.min_balance_on}
                colchao={resumo.data?.buffer}
                onLancar={() => nav("/lancar")}
              />
            )}
          </div>

          <Face className="scale">
            <Scale
              now={saldoHoje}
              other={fimDoMes.end}
              otherDate={fimDoMes.date}
              levels={settings?.settings.health_levels}
            />
          </Face>
        </div>

        <div className="tapewrap">
          <Face paper className="tape">
            <div className="head">
              <Label>Fita do registrador · 30 dias</Label>
              <Label dim>saldo no fim do dia</Label>
            </div>
            <Tape days={dias.slice(0, 30)} today={today} levels={settings?.settings.health_levels} />
          </Face>
        </div>

        <div className="dailywrap">
          <Face className="meter">
            <div className="lblrow">
              <Label>Diário de hoje</Label>
              {porDia && <Label dim>{money(porDia)} por dia</Label>}
            </div>
            <div className="reading" style={{ fontSize: 24 }}>
              {money(diarioDoDia)}
              <small>restam</small>
            </div>
            {porDia && (
              <>
                <div className={`gauge ticks ${estourou ? "estourou" : ""}`} style={{ marginTop: 6 }}>
                  <i style={{ width: `${Math.min(100, (gastoHoje / porDia) * 100)}%` }} />
                </div>
                <div className="sub">
                  gasto hoje {money(gastoHoje)}
                  {estourou && <b className="t-crit"> · passou {money(gastoHoje - porDia)}</b>}
                </div>
              </>
            )}
            {!porDia && (
              <div className="sub">
                Sem diário definido.{" "}
                {/* o diário mudou de Ajustes para Planejado quando virou "os dois números
                    do mês"; o link ficou apontando para a tela onde ele não está mais */}
                <button type="button" className="causelink" onClick={() => nav("/planejado")}>
                  definir no Planejado
                </button>
              </div>
            )}
          </Face>
        </div>

        <div className="logwrap">
          <Face className="log">
            <div className="sec">
              <Label>A pagar</Label>
              <Seg small label="Janela" value={janela} onChange={setJanela} options={JANELAS} />
            </div>

            {aPagar.length === 0 && (
              <Empty title="Nada a pagar">
                {janela === "hoje" ? "Hoje está limpo." : `Nada até ${dayMonth(limite)}.`}
              </Empty>
            )}

            {aPagar.map((item) => {
              const acc = accById.get(item.account_id);
              const stamp = stampLabel({
                status: item.status,
                estimated: item.estimated,
                kind: item.kind,
                bucket: item.bucket,
                overdue: item.overdue,
              });
              return (
                <div className={`line p-${column(item)}`} key={item.tx_id}>
                  <div className="d">
                    {weekday(item.settles_on)}
                    <b>{dayOnly(item.settles_on)}</b>
                  </div>
                  <div className="what">
                    <div className="t">
                      <span className="desc">{item.description || "Sem descrição"}</span>
                      <Stamp text={stamp.text} cls={stamp.cls} />
                    </div>
                    <small>
                      {acc && <AccountTag name={acc.name} color={acc.color} />}{" "}
                      {item.estimated ? "valor estimado" : item.bucket === "daily" ? "diário" : ""}
                    </small>
                  </div>
                  <div className="amt">
                    <span className={item.kind === "income" ? "t-in" : ""}>
                      {item.kind === "income" ? "+" : "−"}
                      {money(item.amount)}
                    </span>
                    {item.estimated ? (
                      <Key onClick={() => setConfirmando(item)}>Confirmar</Key>
                    ) : (
                      <Key
                        onClick={() =>
                          clear.mutate(
                            { id: item.tx_id },
                            {
                              onSuccess: () =>
                                aviso.ok(
                                  `${item.description} · ${item.kind === "income" ? "recebido" : "pago"}`,
                                ),
                              onError: (e) => aviso.erro((e as Error).message),
                            },
                          )
                        }
                        disabled={clear.isPending}
                      >
                        {item.kind === "income" ? "Recebi" : "Pagar"}
                      </Key>
                    )}
                  </div>
                </div>
              );
            })}

            {aPagar.length > 0 && (
              <div className="line total">
                <span />
                <span>
                  {aPagar.length} {aPagar.length === 1 ? "item" : "itens"} até {dayMonth(limite)}
                </span>
                <span className="amt">
                  {totalAPagar < 0 ? "+" : "−"}
                  {money(Math.abs(totalAPagar))}
                </span>
              </div>
            )}
          </Face>
        </div>
      </div>

      {confirmando && (
        <ConfirmarValor
          item={confirmando}
          onClose={() => setConfirmando(null)}
          onConfirm={(amount, paid) => {
            const nome = confirmando.description;
            confirm.mutate(
              { id: confirmando.tx_id, amount, paid },
              {
                onSuccess: () => aviso.ok(`${nome} confirmada em ${money(amount)}`),
                onError: (e) => aviso.erro((e as Error).message),
              },
            );
            setConfirmando(null);
          }}
        />
      )}
    </section>
  );
}

/** O dia em que o próximo dinheiro entra: onde a janela do aviso fecha.
 *
 * Entrada que já caiu hoje não conta — o trecho que interessa é daqui até o dinheiro que
 * ainda FALTA. Entrada atrasada conta: o motor a traz para hoje, e é dela que se espera. */
function primeiraEntrada(dias: Day[], today: string): string | null {
  for (const d of dias) {
    if (d.date < today) continue;
    const entra = d.items.some(
      (i) => i.kind === "income" && !i.synthetic && (d.date > today || i.status !== "cleared"),
    );
    if (entra) return d.date;
  }
  return null;
}

/** O menor saldo até o próximo dinheiro entrar, com o dia, a zona, as causas e o colchão.
 *
 * POR QUE ACOMPANHA O "FIM DO MÊS" EM VEZ DE SUBSTITUIR. Em 07/09 o dono recusou o menor
 * saldo como segunda leitura — "é inútil sem renda fixa, fica infinito" — e pediu o fim do
 * mês no lugar (`HANDOFF.md`). Trocar de volta seria refazer o que ele já recusou. Os dois
 * mostradores que ele aprovou ficam onde estão, e o menor saldo volta como AVISO abaixo
 * deles, com o que nenhum mostrador tinha: o dia, a zona, as causas e o colchão descontado.
 *
 * POR QUE A JANELA FECHA NA PRÓXIMA ENTRADA. `GET /today` calcula o menor saldo sobre o
 * horizonte inteiro (365 dias por padrão). No banco de DEV isso dá −R$ 6.737 em 04/09/2027,
 * com "Aluguel" três vezes seguidas como causa: é exatamente o número que assusta e não
 * significa nada. O trecho que a promessa nomeia é outro — daqui até o dinheiro entrar —,
 * e ele não pode ser infinito, porque a entrada o fecha. Então quem calcula continua sendo
 * o motor, só que sobre ESSA janela: `/projection?frm=hoje&to=<entrada>` devolve
 * `min_balance`, `min_balance_on`, `causes` e `safe_to_spend` prontos. O cliente não faz
 * conta de dinheiro; escolhe o dia em que a janela fecha e lê o que voltou.
 *
 * O colchão vem de `/today` porque ele não muda com simulação e é o mesmo número em todo o
 * painel; a folga sobre ele é o `safe_to_spend` do motor. Continua não existindo "pode
 * gastar": o que está escrito é quanto o fundo fica ACIMA (ou abaixo) do colchão. */
function MenorSaldo({
  scope,
  de,
  ate,
  scenarioId,
  levels,
  colchao,
}: {
  scope: string;
  de: string;
  ate: string;
  scenarioId: string | null;
  levels?: HealthLevel[];
  colchao?: number;
}) {
  const { data, isPending, error, refetch } = useProjection({
    scope,
    frm: de,
    to: ate,
    scenario_id: scenarioId,
  });
  const etiqueta = <Label>Menor saldo até a entrada de {dayMonth(ate)}</Label>;

  if (isPending)
    return (
      <Face className="meter min">
        <div className="lblrow">{etiqueta}</div>
        <Skeleton lines={2} />
      </Face>
    );
  if (error || !data)
    return (
      <Face className="meter min">
        <div className="lblrow">{etiqueta}</div>
        <ErrorBanner
          message={(error as Error)?.message ?? "Não deu para ler o menor saldo."}
          onRetry={() => refetch()}
        />
      </Face>
    );

  const zona = data.days.find((d) => d.date === data.min_balance_on)?.color ?? "amber";
  // `safe_to_spend` do motor = menor saldo − colchão. Aqui ele é lido como distância até o
  // colchão, nunca como permissão.
  const sobreOColchao = data.safe_to_spend;

  return (
    <Face className="meter min" zone={zona}>
      <div className="lblrow">
        {etiqueta}
        <Zone color={zona} levels={levels} value={data.min_balance} />
      </div>
      <div className={`reading t-${zoneClass(zona)}`}>
        {money(data.min_balance)}
        <small>no dia {dayMonth(data.min_balance_on)}</small>
      </div>

      {colchao ? (
        <div className="sub">
          <span className="pair">
            sendo <span className="num">{money(colchao)}</span> de colchão
          </span>
          {" · "}
          {sobreOColchao >= 0 ? (
            <span className="pair">
              <span className="num">{money(sobreOColchao)}</span> acima dele
            </span>
          ) : (
            <span className="pair t-crit">
              <span className="num">{moneyAbs(sobreOColchao)}</span> abaixo dele
            </span>
          )}
        </div>
      ) : null}

      {data.causes.length > 0 && (
        <div className="sub">
          puxam para baixo:{" "}
          {data.causes.map((c, i) => (
            <Fragment key={c.tx_id}>
              {i > 0 && " · "}
              <span className="pair">
                {c.description || "Sem descrição"} <span className="num">−{money(c.amount)}</span>
              </span>
            </Fragment>
          ))}
        </div>
      )}
    </Face>
  );
}

/** Sem nenhuma entrada prevista não existe menor saldo: a projeção só desce e o fundo vira
 * o fim do horizonte. Foi a crítica do dono em 07/09, e ela continua certa — quem não tem
 * renda fixa não tem receita cadastrada. Aqui a tela DIZ isso, em vez de imprimir um número
 * assustador que só mede até onde o motor olhou. A data do horizonte vem de `GET /today`,
 * como prova de que o número existiria e não significaria nada. */
function SemEntrada({
  limite,
  horizonte,
  colchao,
  onLancar,
}: {
  limite: string;
  horizonte?: string;
  colchao?: number;
  onLancar: () => void;
}) {
  return (
    <Face className="meter min">
      <div className="lblrow">
        <Label>Menor saldo</Label>
        <Label dim>sem entrada prevista</Label>
      </div>
      <div className="sub">
        Nada previsto para entrar até {dayMonth(limite)}. Sem dinheiro entrando a projeção só
        desce
        {horizonte ? (
          <>
            , e o fundo seria só o fim do horizonte, em {dayMonthYear(horizonte)} — um número que
            assusta sem dizer onde aperta
          </>
        ) : null}
        . Lance a entrada que você espera e este aviso passa a medir o trecho até ela.
        {colchao ? <> O colchão de {money(colchao)} segue reservado.</> : null}
      </div>
      <div>
        <Key style={ALVO} onClick={onLancar}>
          Lançar uma entrada
        </Key>
      </div>
    </Face>
  );
}

/** O que precisa de ação na janela escolhida: atrasado primeiro, depois por data. */
function itensParaPagar(dias: Day[], today: string, janela: Janela): DayItem[] {
  const limite =
    janela === "hoje" ? today : janela === "semana" ? addDays(today, 7) : monthRange(today).last;
  const itens = dias
    .filter((d) => d.date <= limite)
    .flatMap((d) => d.items)
    .filter((i) => !i.synthetic && i.status !== "cleared" && i.status !== "skipped");
  return itens.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    return a.settles_on.localeCompare(b.settles_on);
  });
}

function ConfirmarValor({
  item,
  onClose,
  onConfirm,
}: {
  item: DayItem;
  onClose: () => void;
  onConfirm: (amount: number, paid: boolean) => void;
}) {
  const [valor, setValor] = useState(item.amount.toFixed(2).replace(".", ","));
  const [pago, setPago] = useState(true);
  const amount = parseMoney(valor);
  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-label="Confirmar valor">
      <div className="panel">
        <div className="handle" />
        <h2>
          <span>Confirmar {item.description}</span>
          <Key icon="x" title="Fechar" onClick={onClose} />
        </h2>
        <div className="sub">
          Estimado em {money(item.amount)} · vence {dayMonth(item.settles_on)}
        </div>
        <Field label="Valor que veio" className="amount">
          <input
            inputMode="decimal"
            value={valor}
            autoFocus
            onChange={(e) => setValor(e.target.value)}
            onFocus={(e) => e.target.select()}
          />
        </Field>
        <label className="switch" style={{ padding: "4px 0" }}>
          <input
            type="checkbox"
            checked={pago}
            onChange={(e) => setPago(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: "var(--ink)" }}
          />
          Já paguei
        </label>
        <div className="acts">
          <Key onClick={onClose}>Cancelar</Key>
          <Key primary disabled={!(amount > 0)} onClick={() => onConfirm(amount, pago)}>
            Confirmar {amount > 0 ? money(amount) : ""}
          </Key>
        </div>
      </div>
    </div>
  );
}
